import React, { useEffect, useMemo, useState } from "react";

import ReactModal from "react-modal";
import { useNavigate } from "react-router-dom";

import {
  BANNER_STATE,
  getBannerState,
  getBannerStateLabel,
  MAX_ACTIVE_BANNERS,
} from "pages/main/BannerManager/bannerState";
import BannerSection from "pages/main/BannerManager/components/BannerSection";
import { useBannerManagerData } from "pages/main/BannerManager/hooks/useBannerManagerData";
import { EditBtn } from "pages/main/BannerManager/index.style";
import { applyDateFilter } from "shared/lib/date/dateFilters";
import { formatToKST, toKstDate } from "shared/lib/date/formatKst";
import { alertDialog, confirmDialog } from "shared/ui/feedback";
import { SearchPanel } from "shared/ui/filters";
import { AddHeader, BaseContainer } from "shared/ui/layout";
import {
  useClientPagination,
  useTableSelection,
} from "shared/ui/table";

ReactModal.setAppElement("#root");

const BANNER_STATUS_OPTIONS = [
  { label: "전체", value: "all" },
  { label: "게시중", value: BANNER_STATE.PUBLISHED },
  { label: "미게시", value: BANNER_STATE.UNPUBLISHED },
  { label: "게시종료", value: BANNER_STATE.ENDED },
];
const QUICK_RANGE_OPTIONS = [
  { label: "오늘", value: 0 },
  { label: "1주일", value: 1 },
  { label: "1개월", value: 2 },
  { label: "3개월", value: 3 },
  { label: "6개월", value: 4 },
  { label: "1년", value: 5 },
];

// 제목·구분(상태)·기간을 적용해 배너 목록을 필터. createdAt은 서버 UTC라 KST 보정.
const filterBanners = (banners, { keyword, status, dateButtonClicked, startDate, endDate }) =>
  applyDateFilter({
    data: banners.filter((banner) => {
      const includesSearchTerm = banner.title?.includes(keyword) ?? false;
      const matchesStatus =
        status === "all" || getBannerState(banner) === status;
      return includesSearchTerm && matchesStatus;
    }),
    dateButtonClicked,
    startDate,
    endDate,
    getDate: (banner) => toKstDate(banner.createdAt) ?? new Date(NaN),
  });

function BannerManager() {
  const navigate = useNavigate();
  const {
    activeBanners,
    deletionInProgress,
    deleteSelectedEndedBanners,
    endedBanners,
    itemsPerPageend,
    itemsPerPageposting,
    setHideValues,
    unpublishedBanners,
  } = useBannerManagerData();
  // 검색 결과를 스냅샷으로 저장하면 옵티미스틱 갱신(미게시/삭제)이 화면에 반영되지
  // 않는다. 조건만 저장하고 목록은 항상 최신 캐시에서 파생하면, 캐시가 바뀔 때마다
  // 필터된 화면도 자동으로 따라온다.
  const [searchArgs, setSearchArgs] = useState(null);

  const filteredActiveBanners = useMemo(
    () => (searchArgs ? filterBanners(activeBanners, searchArgs) : activeBanners),
    [activeBanners, searchArgs],
  );
  const filteredUnpublishedBanners = useMemo(
    () =>
      searchArgs
        ? filterBanners(unpublishedBanners, searchArgs)
        : unpublishedBanners,
    [unpublishedBanners, searchArgs],
  );
  const filteredEndedBanners = useMemo(
    () => (searchArgs ? filterBanners(endedBanners, searchArgs) : endedBanners),
    [endedBanners, searchArgs],
  );

  const activeSelection = useTableSelection(filteredActiveBanners);
  const unpublishedSelection = useTableSelection(filteredUnpublishedBanners);
  const endedSelection = useTableSelection(filteredEndedBanners);
  const activePagination = useClientPagination(filteredActiveBanners, {
    pageSize: itemsPerPageposting,
  });
  const unpublishedPagination = useClientPagination(
    filteredUnpublishedBanners,
    { pageSize: itemsPerPageend },
  );
  const endedPagination = useClientPagination(filteredEndedBanners, {
    pageSize: itemsPerPageend,
  });

  useEffect(() => {
    const admintoken = localStorage.getItem("admintoken");

    if (!admintoken) {
      alertDialog("로그인이 필요합니다.");
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  // 서버는 타임존 없는 KST 벽시계를 그대로 내려주므로 변환 없이 포맷만 한다.
  const formatKoreanDateTime = (value) => formatToKST(value);

  const columns = useMemo(
    () => [
      { Header: "No.", accessor: "id" },
      {
        Header: "이미지",
        accessor: "webImage",
        Cell: ({ value, row }) =>
          value ? (
            <img
              src={value}
              alt="배너 미리보기"
              style={{ maxWidth: "500px", maxHeight: "50px" }}
              onError={(e) => {
                const fallback =
                  row.original?.mobileImage ||
                  row.original?.imageUrl ||
                  row.original?.image ||
                  row.original?.thumbnail ||
                  "";
                if (fallback && e.currentTarget.src !== fallback) {
                  e.currentTarget.src = fallback;
                  return;
                }
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <span>-</span>
          ),
      },
      {
        Header: "배너 타이틀",
        accessor: "title",
        Cell: ({ value }) => {
          const text = value ?? "";
          const truncatedTitle =
            text.length > 22 ? text.substring(0, 22) + "..." : text;

          return (
            <span title={text} style={{ fontFamily: "monospace" }}>
              {truncatedTitle}
            </span>
          );
        },
      },
      {
        Header: "등록일",
        accessor: "createdAt",
        Cell: ({ value }) => formatKoreanDateTime(value),
      },
      {
        Header: "상태",
        id: "state",
        accessor: (row) => getBannerStateLabel(row),
        Cell: ({ value }) => (
          <span style={{ display: "block", textAlign: "center" }}>{value}</span>
        ),
      },
      {
        Header: "설정",
        id: "actions",
        accessor: (row) => row.id,
        Cell: ({ row }) => (
          <EditBtn
            onClick={() => navigate(`/main/banner/edit/${row.original.id}`)}
          >
            설정
          </EditBtn>
        ),
      },
    ],
    [navigate],
  );

  const handleCreateBannerClick = () => {
    navigate("/main/banner/register");
  };

  const handleSearchFilters = (filters) => {
    const { keyword, chips, dateButtonClicked, startDate, endDate } = filters;
    const args = {
      keyword,
      status: chips?.status ?? "all",
      dateButtonClicked,
      startDate,
      endDate,
    };
    const hasMatch = [activeBanners, unpublishedBanners, endedBanners].some(
      (list) => filterBanners(list, args).length > 0,
    );

    if (!hasMatch) {
      alertDialog(
        "해당 검색어를 찾을 수 없습니다.\n검색어를 다시 확인하시고 검색해주세요.",
      );
      return;
    }

    setSearchArgs(args);
  };

  const handleResetFilters = () => {
    setSearchArgs(null);
  };

  // 게시중 → 미게시 처리 (홈 노출에서 내림)
  const handleUnpublishClick = async () => {
    if (activeSelection.count === 0) return;
    const confirmed = await confirmDialog(
      "해당 배너를 미게시 처리하시겠습니까?\n홈페이지 메인 게시가 중단됩니다.",
    );
    if (confirmed) {
      await setHideValues(activeSelection.selectedRows, true);
      activeSelection.clear();
    }
  };

  // 미게시 → 게시 처리. 게시 시 게시중(노출)이 되는 건만 3개 한도에 포함해 검사한다.
  const handlePublishClick = async () => {
    if (unpublishedSelection.count === 0) return;

    const wouldBePublished = unpublishedSelection.selectedRows.filter(
      (row) => getBannerState({ ...row, hide: false }) === BANNER_STATE.PUBLISHED,
    ).length;
    if (activeBanners.length + wouldBePublished > MAX_ACTIVE_BANNERS) {
      alertDialog(
        `게시 중인 배너는 최대 ${MAX_ACTIVE_BANNERS}개까지 가능합니다.\n현재 ${activeBanners.length}개가 게시 중입니다.`,
      );
      return;
    }

    const confirmed = await confirmDialog(
      "해당 배너를 게시 처리하시겠습니까?\n홈페이지 메인에 게시됩니다.",
    );
    if (confirmed) {
      await setHideValues(unpublishedSelection.selectedRows, false);
      unpublishedSelection.clear();
    }
  };

  const removeBanners = async (selection) => {
    if (selection.count === 0 || deletionInProgress) return;

    const shouldRemove = await confirmDialog(
      "해당 배너를 삭제하시겠습니까?\n삭제 시 정보가 사라집니다. 그래도 삭제하시겠습니까?",
    );
    if (!shouldRemove) return;

    try {
      await deleteSelectedEndedBanners(selection.selectedRows);
      selection.clear();
    } catch (error) {
      alertDialog("There was an error deleting the items. Please try again.");
      // console.error("Error in handledeletedClick:", error);
    }
  };

  return (
    <BaseContainer>
      <AddHeader
        pageName="배너 관리"
        onClick={handleCreateBannerClick}
        buttonValue="배너 등록하기"
      />
      <SearchPanel onSearch={handleSearchFilters} onReset={handleResetFilters}>
        <SearchPanel.Keyword label="조건 검색" />
        <SearchPanel.Chips
          name="status"
          label="구분"
          options={BANNER_STATUS_OPTIONS}
          deselectTo="all"
        />
        <SearchPanel.QuickRange
          label="게시 기간"
          options={QUICK_RANGE_OPTIONS}
        />
        <SearchPanel.DateRange />
      </SearchPanel>

      <BannerSection
        actions={[
          {
            label: "미게시 처리",
            onClick: handleUnpublishClick,
            disabled: activeSelection.count === 0,
          },
        ]}
        columns={columns}
        count={filteredActiveBanners.length}
        pagination={activePagination}
        selection={activeSelection}
        title="게시 중인 배너"
      />

      <BannerSection
        actions={[
          {
            label: "게시 처리",
            onClick: handlePublishClick,
            disabled: unpublishedSelection.count === 0,
          },
          {
            label: "영구 삭제",
            onClick: () => removeBanners(unpublishedSelection),
            disabled: unpublishedSelection.count === 0 || deletionInProgress,
          },
        ]}
        columns={columns}
        count={filteredUnpublishedBanners.length}
        pagination={unpublishedPagination}
        selection={unpublishedSelection}
        title="미게시 배너"
      />

      <BannerSection
        actions={[
          {
            label: "영구 삭제",
            onClick: () => removeBanners(endedSelection),
            disabled: endedSelection.count === 0 || deletionInProgress,
          },
        ]}
        columns={columns}
        count={filteredEndedBanners.length}
        pagination={endedPagination}
        selection={endedSelection}
        title="게시 종료된 배너"
      />
    </BaseContainer>
  );
}

export default BannerManager;
